/**
 * ScenarioSelectionModal.js — Full-screen modal to pick a scenario to load.
 *
 * Lists all saved scenarios for the current account, showing each scenario's
 * name, home/away team names, and creation date. Includes a "Refresh" button
 * to re-fetch the list and a "+ New Scenario" button to create a blank one.
 *
 * Role in the app: shown when the user clicks "Load" in the
 * ScenarioManagementMenu (premium only). Calls `onSelect(scenario)` or
 * `onNew()` and then closes.
 *
 * Props:
 * - scenarios:  array of scenario objects (from session, populated with teams)
 * - onSelect:   (scenario) => void
 * - onNew:      () => void — create a new empty scenario
 * - onClose:    () => void
 * - onRefresh:  () => void — re-fetch the scenario list
 * - refreshing: boolean — disables the Refresh button while loading
 * - onRename:       (scenario, newName) => void — inline rename (optional)
 * - onDelete:       (scenario) => void — delete after confirm (optional)
 * - onTogglePublic: (scenario) => void — flip the share flag (optional)
 */

import React, { useState } from 'react';
import { useI18n } from './i18n';
import NameModal from './NameModal';

/**
 * ScenarioSelectionModal component.
 *
 * @param {Object}   props
 * @param {Array}    [props.scenarios=[]] - Scenarios to display.
 * @param {Function} props.onSelect       - Called with the chosen scenario object.
 * @param {Function} props.onNew          - Called to create a new scenario.
 * @param {Function} props.onClose        - Called to dismiss the modal.
 * @param {Function} props.onRefresh      - Called to re-fetch scenarios.
 * @param {boolean}  props.refreshing     - Whether the list is currently loading.
 * @returns {JSX.Element} The modal markup.
 */
function ScenarioSelectionModal({
  scenarios = [], onSelect, onNew, onClose, onRefresh, refreshing,
  onRename = null, onDelete = null, onTogglePublic = null,
}) {
  const { t } = useI18n();
  // Renaming used to pop a browser prompt() — an untranslatable strip over the
  // list, with the current name in a field you had to clear by hand.
  const [renameState, setRenameState] = useState(null);
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div className="kv-selection-modal" style={modalStyle} onClick={e => e.stopPropagation()}>
        {/* ── Header: title + refresh/close buttons ── */}
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: '1.2rem' }}>{t('scenario.loadTitle')}</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              style={{ ...closeBtnStyle, width: 'auto', padding: '0 0.6rem', fontSize: '0.8rem', background: '#1e293b' }}
              onClick={onRefresh}
              disabled={refreshing}
              title={t('scenario.refreshHint')}
            >
              {refreshing ? t('scenario.loading') : t('scenario.refresh')}
            </button>
            <button style={closeBtnStyle} onClick={onClose}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          </div>
        </div>
        {/* ── Manage hint: the three row icons say nothing on a touch screen,
            where a title attribute never shows. One line, only when the row
            actions exist at all. ── */}
        {(onRename || onTogglePublic || onDelete) && (
          <div style={{ fontSize: '0.72rem', color: '#94a3b8', textAlign: 'center', margin: '2px 0 6px' }}>
            {t('scenario.manageHint')}
          </div>
        )}
        {/* ── Scenario list (or empty-state message) ── */}
        <div style={listStyle}>
          {scenarios.length === 0 ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem 1rem' }}>
              {t('scenario.noneYet')}
            </div>
          ) : (
            scenarios.map(scen => (
              <div
                key={scen._id || scen.id}
                onClick={() => { onSelect(scen); onClose(); }}
                style={cardStyle}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                    <div style={{ fontWeight: 700, color: '#38bdf8', fontSize: '0.95rem' }}>
                      {scen.name}
                      {scen.isPublic && (
                        <span style={{
                          marginLeft: 6, padding: '1px 6px', borderRadius: 8, fontSize: 10,
                          background: '#14532d', color: '#86efac', fontWeight: 700,
                        }}>{t('scenario.publicBadge')}</span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                      {scen.createdAt ? ` · ${new Date(scen.createdAt).toLocaleDateString()}` : ''}
                    </div>
                  </div>
                  {/* ── Manage actions: rename / share / delete (stop the card click) ── */}
                  {(onRename || onTogglePublic || onDelete) && (
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                      {onRename && (
                        <button type="button" title={t('scenario.rename')} aria-label={t('scenario.rename')} style={iconBtnStyle}
                          onClick={() => setRenameState({ scen, defaultValue: scen.name })}>✎</button>
                      )}
                      {onTogglePublic && (
                        <button type="button" title={scen.isPublic ? t('scenario.makePrivate') : t('scenario.makePublic')}
                          aria-label={scen.isPublic ? t('scenario.makePrivate') : t('scenario.makePublic')} style={iconBtnStyle}
                          onClick={() => onTogglePublic(scen)}>{scen.isPublic ? '🌍' : '🔒'}</button>
                      )}
                      {onDelete && (
                        <button type="button" title={t('confirm.deleteScenario')} aria-label={t('confirm.deleteScenario')}
                          style={{ ...iconBtnStyle, color: '#f87171' }}
                          onClick={() => onDelete(scen)}>🗑</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        {/* ── Footer: new scenario button ── */}
        <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #334155' }}>
          <button
            style={newBtnStyle}
            onClick={() => { onNew(); onClose(); }}
          >
            + {t('scenario.newOne')}
          </button>
        </div>
      </div>
      {renameState && (
        <NameModal
          title={t('name.renameScenario')}
          label={t('name.renameHint')}
          defaultValue={renameState.defaultValue}
          confirmLabel={t('scenario.rename')}
          onSubmit={(name) => {
            const scen = renameState.scen;
            setRenameState(null);
            if (name && name !== scen.name) onRename(scen, name);
          }}
          onCancel={() => setRenameState(null)}
        />
      )}
    </div>
  );
}

// ── Inline styles ──────────────────────────────────

const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 9998,
  background: 'rgba(0,0,0,0.7)', display: 'flex',
  justifyContent: 'center', alignItems: 'center',
};

const modalStyle = {
  background: '#1e293b', borderRadius: '12px', padding: '1.5rem',
  width: '100%', maxWidth: '480px',
  // dvh-backed var: the visible viewport, so the modal never lands under the
  // mobile browser chrome in landscape
  maxHeight: 'calc(var(--kv-viewport-h) * 0.8)', overflow: 'hidden',
  display: 'flex', flexDirection: 'column',
};

const headerStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #334155',
};

const closeBtnStyle = {
  background: '#475569', border: 'none', borderRadius: '6px',
  color: '#fff', width: '30px', height: '30px', cursor: 'pointer', fontSize: '0.9rem',
};

const listStyle = {
  overflowY: 'auto', flex: 1, minHeight: 0, // minHeight:0 lets the list shrink below its content so the 80vh cap scrolls instead of clipping
  display: 'flex', flexDirection: 'column', gap: '0.5rem',
};

const cardStyle = {
  padding: '0.75rem', borderRadius: '8px', cursor: 'pointer',
  background: '#0f172a', border: '1px solid #334155', transition: 'all 0.15s',
};

const newBtnStyle = {
  width: '100%', padding: '0.6rem', background: '#3b82f6', color: '#fff',
  border: 'none', borderRadius: '6px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
};

/* Small per-card manage buttons (rename / share / delete). */
const iconBtnStyle = {
  width: 28, height: 28, borderRadius: 6, cursor: 'pointer', fontSize: 13,
  background: '#1e293b', border: '1px solid #334155', color: '#cbd5e1',
};

export default ScenarioSelectionModal;
